import Business from '@mui/icons-material/Business'
import CompareArrows from '@mui/icons-material/CompareArrows'
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline'
import Public from '@mui/icons-material/Public'
import SupportAgent from '@mui/icons-material/SupportAgent'
import Tune from '@mui/icons-material/Tune'
import {
  Button,
  Typography,
  Stack,
  Box,
  Divider,
  alpha,
  Alert,
} from '@mui/material'
import { green, lightBlue, orange } from '@mui/material/colors'
import { OverlayState } from '@/hooks/useOverlay'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'

type SectionHeaderProps = {
  icon: React.ReactNode
  color: string
  title: string
}

function SectionHeader({ icon, color, title }: SectionHeaderProps) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 32,
          height: 32,
          borderRadius: 2,
          bgcolor: alpha(color, 0.12),
          color,
        }}
      >
        {icon}
      </Stack>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
  )
}

type POMatchHelpDialogProps = {
  overlay: OverlayState
}

export default function POMatchHelpDialog({ overlay }: POMatchHelpDialogProps) {
  return (
    <Dialog
      title="Purchase Order Matching"
      subtitle="How AI-powered invoice reconciliation works"
      {...overlay}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            Purchase Order Matching uses AI to automatically link invoice line
            items to their corresponding purchase order line items in your
            external AP system — reducing manual effort and errors in accounts
            payable.
          </Typography>

          <Divider />

          {/* How We Match */}
          <Stack spacing={1.5}>
            <SectionHeader
              icon={<CompareArrows sx={{ fontSize: 18 }} />}
              color={green[600]}
              title="How We Match"
            />
            <Typography variant="body2" color="text.secondary">
              Our AI evaluates multiple signals to find the best matches:
            </Typography>
            <Stack spacing={1.5} sx={{ pl: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Semantic Descriptions
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Items don&apos;t need identical wording — descriptions are
                  compared by meaning, not exact text.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Amounts &amp; Quantities
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Checked with tolerances for small variances like tax,
                  shipping, or rounding.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  PO Number Prioritization
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PO numbers referenced on the invoice are used to prioritize
                  candidate purchase orders.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Line Item Context
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  If most lines match a specific PO, ambiguous lines are more
                  likely to belong there too.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Previous Match History
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Already-matched invoices are tracked to keep remaining
                  quantities and totals accurate.
                </Typography>
              </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              It is not limited to only these signals — the AI model is designed
              to infer complex relationships from any data presented to it. All
              considered, it acts much like humans do when reconciling invoices,
              but with the speed and consistency of a machine.
            </Typography>
          </Stack>

          <Divider />

          {/* Custom Instructions */}
          <Stack spacing={1.5}>
            <SectionHeader
              icon={<Tune sx={{ fontSize: 18 }} />}
              color={orange[600]}
              title="Custom Instructions"
            />
            <Typography variant="body2" color="text.secondary">
              Fine-tune matching behavior with custom instructions at two
              levels:
            </Typography>
            <Stack spacing={1} sx={{ m: 0, pl: 0.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Public
                  sx={{ fontSize: 18, mt: '2px', color: 'text.secondary' }}
                />
                <Typography variant="body2" color="text.secondary">
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    <strong>Global Instructions</strong>
                  </Box>{' '}
                  apply to all companies in this project. Use these for rules
                  that should always be followed across the board.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Business
                  sx={{ fontSize: 18, mt: '2px', color: 'text.secondary' }}
                />
                <Typography variant="body2" color="text.secondary">
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    <strong>Company-Specific Instructions</strong>
                  </Box>{' '}
                  apply to individual companies. Use these for any
                  company-specific matching rules for vendors, categories, or
                  other attributes.
                </Typography>
              </Stack>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              Example: &ldquo;Always prioritize matching by Part Number over
              Description&rdquo; or &ldquo;Do not match if the amount variance
              exceeds 5%.&rdquo;
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Custom instructions are included in the AI model prompt to guide
              matching decisions. They can be updated at any time to adapt to
              changing business rules or to improve matching accuracy based on
              observed results.
            </Typography>
          </Stack>

          <Divider />

          {/* When Does It Run? */}
          <Stack spacing={1.5}>
            <SectionHeader
              icon={<PlayCircleOutline sx={{ fontSize: 18 }} />}
              color={lightBlue[600]}
              title="When Does It Run?"
            />
            <Typography variant="body2" color="text.secondary">
              PO matching runs automatically as part of the document processing
              workflow. When an invoice enters the workflow configured for PO
              matching, it&apos;s matched against candidate purchase orders and
              results appear in the validation screen.
            </Typography>
            <Alert severity="info" icon={<SupportAgent />}>
              If PO matching isn&apos;t running or you need help with workflow
              configuration, contact <strong>Pixydocs Support</strong>.
            </Alert>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogFooter>
        <Button onClick={overlay.close}>Close</Button>
      </DialogFooter>
    </Dialog>
  )
}
